/**
 * Hook for managing market watchlist (bookmarks)
 *
 * Uses /api/social/watchlist endpoints
 */

import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '@/lib/authFetch';
import { usePrivy } from '@privy-io/react-auth';
import { trackRecommendation } from '@/lib/recommendation-tracker';

export interface WatchlistItem {
  id: string;
  user_id: string;
  market_slug: string;
  market_title: string | null;
  condition_id: string | null;
  source: 'polymarket' | 'kalshi' | 'predict';
  created_at: string;
}

const WATCHLIST_CACHE_KEY = 'frenflow-watchlist-cache';

function getCachedWatchlist(): WatchlistItem[] {
  try {
    const stored = localStorage.getItem(WATCHLIST_CACHE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

function setCachedWatchlist(items: WatchlistItem[]) {
  try { localStorage.setItem(WATCHLIST_CACHE_KEY, JSON.stringify(items)); } catch {}
}

export function useMarketWatchlist() {
  const { authenticated, ready } = usePrivy();
  // Initialize from localStorage cache for instant UI (no flash of missing Bookmarks tab)
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>(() => getCachedWatchlist());
  const [watchlistSlugs, setWatchlistSlugs] = useState<Set<string>>(() => new Set(getCachedWatchlist().map(i => i.market_slug)));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch watchlist on mount and when auth changes
  const fetchWatchlist = useCallback(async () => {
    // While Privy is loading, keep localStorage cache intact (prevents pill flash)
    if (!ready) return;
    if (!authenticated) {
      setWatchlist([]);
      setWatchlistSlugs(new Set());
      setCachedWatchlist([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await authFetch('/api/social/watchlist');
      if (res.ok) {
        const data = await res.json();
        const items = data.watchlist || [];
        setWatchlist(items);
        setWatchlistSlugs(new Set(items.map((item: WatchlistItem) => item.market_slug)));
        setCachedWatchlist(items);
      } else {
        setError('Failed to load watchlist');
      }
    } catch (err) {
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }, [ready, authenticated]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  // Check if a market is in watchlist
  const isInWatchlist = useCallback((slug: string) => {
    return watchlistSlugs.has(slug);
  }, [watchlistSlugs]);

  // Add market to watchlist
  const addToWatchlist = useCallback(async (marketSlug: string, marketTitle?: string, conditionId?: string, source?: string) => {
    if (!authenticated) return false;

    try {
      const res = await authFetch('/api/social/watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketSlug, marketTitle, conditionId, source: source || 'polymarket' }),
      });

      if (!res.ok) {
        console.error('[Watchlist] Add failed:', res.status, await res.text().catch(() => ''));
        return false;
      }

      const data = await res.json();
      // Update state locally instead of refetching entire watchlist
      setWatchlistSlugs(prev => new Set([...prev, marketSlug]));
      if (data.item) {
        setWatchlist(prev => {
          const next = [data.item, ...prev];
          setCachedWatchlist(next);
          return next;
        });
      } else {
        // Fallback: construct a minimal item
        setWatchlist(prev => {
          const next = [{
            id: `temp-${Date.now()}`,
            user_id: '',
            market_slug: marketSlug,
            market_title: marketTitle || null,
            condition_id: conditionId || null,
            source: (source || 'polymarket') as WatchlistItem['source'],
            created_at: new Date().toISOString(),
          }, ...prev];
          setCachedWatchlist(next);
          return next;
        });
      }
      trackRecommendation(marketSlug, 'watchlist_add');
      return true;
    } catch (err) {
      console.error('[Watchlist] Add error:', err);
      return false;
    }
  }, [authenticated]);

  // Remove market from watchlist
  const removeFromWatchlist = useCallback(async (marketSlug: string, source?: string) => {
    if (!authenticated) return false;

    try {
      const qs = source ? `?source=${source}` : '';
      const res = await authFetch(`/api/social/watchlist/${encodeURIComponent(marketSlug)}${qs}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        // Optimistic update
        setWatchlistSlugs(prev => {
          const next = new Set(prev);
          next.delete(marketSlug);
          return next;
        });
        setWatchlist(prev => {
          const next = prev.filter(item => item.market_slug !== marketSlug);
          setCachedWatchlist(next);
          return next;
        });
        // Track for recommendation system
        trackRecommendation(marketSlug, 'watchlist_remove');
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [authenticated]);

  // Toggle watchlist status
  const toggleWatchlist = useCallback(async (marketSlug: string, marketTitle?: string, conditionId?: string, source?: string) => {
    if (isInWatchlist(marketSlug)) {
      return removeFromWatchlist(marketSlug, source);
    } else {
      return addToWatchlist(marketSlug, marketTitle, conditionId, source);
    }
  }, [isInWatchlist, addToWatchlist, removeFromWatchlist]);

  const hasBookmarksForSource = useCallback((source: string) => {
    return watchlist.some(item => (item.source || 'polymarket') === source);
  }, [watchlist]);

  return {
    watchlist,
    watchlistSlugs,
    loading,
    error,
    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    toggleWatchlist,
    refetch: fetchWatchlist,
    hasBookmarks: watchlist.length > 0,
    hasBookmarksForSource,
  };
}
